// ImageResizer.jsx (Fully updated code with similar stylings and layout)

import React, { useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

function ImageResizer() {
  const [image, setImage] = useState(null);
  const [resizedImage, setResizedImage] = useState(null);
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [usePreset, setUsePreset] = useState(true);
  const [presetDimensions, setPresetDimensions] = useState("1920x1080");

  const presetOptions = [
    { value: "1920x1080", label: "1920 x 1080 (Full HD)" },
    { value: "2560x1440", label: "2560 x 1440 (2K)" },
    { value: "3840x2160", label: "3840 x 2160 (4K)" },
    { value: "1280x720", label: "1280 x 720 (HD)" },
    { value: "1080x1080", label: "1080 x 1080 (Square)" },
  ];

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result);
      setResizedImage(null);
    };
    reader.readAsDataURL(file);
  };

  const handleResize = () => {
    if (!image) {
      alert("Please upload an image first.");
      return;
    }

    let width, height;
    if (usePreset) {
      [width, height] = presetDimensions.split("x").map(Number);
    } else {
      width = parseInt(customWidth, 10);
      height = parseInt(customHeight, 10);
      if (!width || !height || width <= 0 || height <= 0) {
        alert("Please enter valid dimensions.");
        return;
      }
    }

    const img = new Image();
    img.src = image;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      setResizedImage(canvas.toDataURL("image/png"));
    };
  };

  const handleDownload = () => {
    if (!resizedImage) return;
    const link = document.createElement("a");
    link.href = resizedImage;
    link.download = "resized-image.png";
    link.click();
  };

  const handleReset = () => {
    setImage(null);
    setResizedImage(null);
    setCustomWidth("");
    setCustomHeight("");
    setUsePreset(true);
    setPresetDimensions("1920x1080");
  };

  return (
    <Container maxWidth="md" sx={{ mt: 1 }}>
      <Box
        sx={{
          mb: 3,
          py: 2,
          textAlign: "center",
          backgroundColor: "#f2f2f2",
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          Image Resizer
        </Typography>
      </Box>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
        }}
      >
        <Grid container spacing={4}>
          {/* Left Column: Upload Image */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Upload Image
              </Typography>
              <Box
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  height: 200,
                  overflow: "hidden",
                }}
              >
                {image ? (
                  <img
                    src={image}
                    alt="Uploaded"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography color="text.secondary">
                    No image selected
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                color="primary"
                sx={{ borderRadius: "8px" }}
                component="label"
                startIcon={<UploadFileIcon />}
              >
                Choose File
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </Button>
            </Paper>
          </Grid>

          {/* Right Column: Resize Options & Preview */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Resize Options
              </Typography>
              <Button
                variant="contained"
                color="info"
                onClick={() => setUsePreset((prev) => !prev)}
                sx={{ mb: 2, borderRadius: "8px", textTransform: "none" }}
              >
                {usePreset ? "Switch to Custom Size" : "Switch to Preset Size"}
              </Button>

              {usePreset ? (
                <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="preset-dimension-label">
                    Preset Dimensions
                  </InputLabel>
                  <Select
                    labelId="preset-dimension-label"
                    label="Preset Dimensions"
                    value={presetDimensions}
                    onChange={(e) => setPresetDimensions(e.target.value)}
                  >
                    {presetOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <TextField
                    label="Width (px)"
                    type="number"
                    value={customWidth}
                    onChange={(e) => setCustomWidth(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Height (px)"
                    type="number"
                    value={customHeight}
                    onChange={(e) => setCustomHeight(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-start",
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ borderRadius: "8px" }}
                  onClick={handleResize}
                  disabled={!image}
                >
                  Resize
                </Button>
                {image && (
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{ borderRadius: "8px" }}
                    startIcon={<RestartAltIcon />}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                )}
              </Box>

              {/* Preview */}
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Resized Preview
                  </Typography>
                  <Box
                    sx={{
                      border: "2px dashed #ccc",
                      borderRadius: 2,
                      height: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {resizedImage ? (
                      <img
                        src={resizedImage}
                        alt="Resized"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography color="text.secondary">
                        No resized image yet
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                {resizedImage && (
                  <Box sx={{ p: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      sx={{ borderRadius: "8px", width: "100%" }}
                    >
                      Download
                    </Button>
                  </Box>
                )}
              </Card>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

export default ImageResizer;

/*
# Created/Modified files during execution:
ImageResizer.jsx
*/
